/**
 * Rent versus buy: one implementation of the arithmetic.
 *
 * The calculator on /calculate/rent-vs-buy and the figures inside blog
 * posts must answer identically, so both call this. The method, stated
 * once:
 *
 *   cost of owning  = interest paid to date
 *                   + what the down payment would have earned on deposit
 *                   - the flat's price growth to date
 *   cost of renting = rent paid to date, compounded by rent growth
 *   break-even year = the first year owning falls below renting
 *
 * Repaid principal is deliberately NOT a cost: it stays the buyer's
 * equity. The down payment is counted as a missed opportunity instead,
 * because parked in a deposit it would have earned interest.
 */

/** Level monthly payment for a loan (0% rate degrades to straight line). */
export function annuity(loan: number, ratePct: number, years: number): number {
  if (loan <= 0 || years <= 0) return 0;
  const i = ratePct / 100 / 12;
  const n = years * 12;
  return i === 0 ? loan / n : (loan * i * (1 + i) ** n) / ((1 + i) ** n - 1);
}

export interface YearRow {
  year: number;
  /** Cumulative cost of owning by the end of this year. */
  own: number;
  /** Cumulative rent paid by the end of this year. */
  rent: number;
}

export interface RentBuyInput {
  price: number;
  /** Monthly rent for the same flat. */
  rent: number;
  /** Down payment in money, not per cent. */
  downAmount: number;
  ratePct: number;
  /** Loan term in years. */
  years: number;
  /** Annual price growth, per cent. Default 0: we forecast nothing. */
  growthPct?: number;
  /** Annual rent growth, per cent. */
  rentGrowthPct?: number;
  /** Deposit rate the down payment could have earned, per cent. */
  depositPct?: number;
  /** How many years to project. */
  horizonYears?: number;
}

export interface RentBuyResult {
  loan: number;
  monthly: number;
  rows: YearRow[];
  /** First year owning costs less than renting, null if it never does. */
  breakEven: number | null;
}

export function rentVsBuy({
  price,
  rent,
  downAmount,
  ratePct,
  years,
  growthPct = 0,
  rentGrowthPct = 0,
  depositPct = 0,
  horizonYears = 30,
}: RentBuyInput): RentBuyResult {
  const loan = Math.max(0, price - downAmount);
  const monthly = annuity(loan, ratePct, years);
  const i = ratePct / 100 / 12;
  const rows: YearRow[] = [];
  let balance = loan;
  let interestPaid = 0;
  let rentPaid = 0;
  let breakEven: number | null = null;
  for (let y = 1; y <= horizonYears; y++) {
    for (let m = 0; m < 12; m++) {
      if (y <= years && balance > 0) {
        const interest = balance * i;
        const principal = Math.min(balance, monthly - interest);
        interestPaid += interest;
        balance -= principal;
      }
    }
    rentPaid += rent * 12 * Math.pow(1 + rentGrowthPct / 100, y - 1);
    const opportunity = downAmount * (Math.pow(1 + depositPct / 100, y) - 1);
    const appreciation = price * (Math.pow(1 + growthPct / 100, y) - 1);
    const own = interestPaid + opportunity - appreciation;
    rows.push({ year: y, own, rent: rentPaid });
    if (breakEven == null && own <= rentPaid) breakEven = y;
  }
  return { loan, monthly, rows, breakEven };
}
